import { Link, router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { analyticsMissionPath, missionsPath } from "@/routes"
import type { MissionAnalyticsTopMissionRow } from "@/types/mission-analytics"

/**
 * OKS-63 — Top-5 best-performers table for the cross-mission overview.
 *
 * Two layouts via the same data:
 *   - **≥md (table)**: 5-column table; row click navigates to the
 *     per-mission deep-dive at `analyticsMissionPath(id)`.
 *   - **<md (card list)**: each mission renders as a vertical Card
 *     stack (title + 3 stat rows). UX Spec §5 mobile reflow — table
 *     would truncate rate cells in horizontal scroll.
 *
 * Empty state: when `topMissions.length === 0` (an `empty_in_window`
 * brand), render the empty-card variant in the same surface.
 */
export interface AnalyticsTopMissionsTableProps {
  topMissions: MissionAnalyticsTopMissionRow[]
  className?: string
}

export function AnalyticsTopMissionsTable({
  topMissions,
  className,
}: AnalyticsTopMissionsTableProps) {
  const { t, i18n } = useTranslation()

  const isEmpty = topMissions.length === 0

  return (
    <Card
      data-testid="analytics-overview-top-missions"
      className={cn("flex flex-col gap-3 p-4 sm:p-6", className)}
    >
      <CardHeader className="p-0">
        <CardTitle className="text-foreground text-base font-semibold">
          {t("analytics.top_missions.title")}
        </CardTitle>
        <p className="text-muted-foreground text-sm">
          {t("analytics.top_missions.subtitle")}
        </p>
      </CardHeader>

      <CardContent className="p-0">
        {isEmpty ? (
          <EmptyState />
        ) : (
          <>
            <DesktopTable
              rows={topMissions}
              locale={i18n.language}
              t={t}
              className="hidden md:block"
            />
            <MobileCards
              rows={topMissions}
              locale={i18n.language}
              t={t}
              className="md:hidden"
            />
            <div className="mt-4 text-right">
              <Link
                href={missionsPath()}
                className="text-primary text-sm font-medium hover:underline"
                data-testid="analytics-overview-top-missions-view-all"
              >
                {t("analytics.top_missions.view_all")}
              </Link>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

interface RowsProps {
  rows: MissionAnalyticsTopMissionRow[]
  locale: string
  // i18next's `t` is hard to type for downstream helpers — string is fine.
  t: (key: string) => string
  className?: string
}

function DesktopTable({ rows, locale, t, className }: RowsProps) {
  return (
    <div
      className={className}
      data-testid="analytics-overview-top-missions-table"
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("analytics.top_missions.columns.title")}</TableHead>
            <TableHead>{t("analytics.top_missions.columns.type")}</TableHead>
            <TableHead className="text-right">
              {t("analytics.top_missions.columns.total_collabs")}
            </TableHead>
            <TableHead className="text-right">
              {t("analytics.top_missions.columns.completion_rate")}
            </TableHead>
            <TableHead className="text-right">
              {t("analytics.top_missions.columns.approved_submissions")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.id}
              className="hover:bg-muted/50 cursor-pointer"
              data-testid={`analytics-overview-top-mission-row`}
              // UX Spec §4: whole row is the click target, not just the
              // title cell. `<a>` cannot wrap a `<tr>` (HTML semantics);
              // we route via Inertia's router on the row click instead.
              // Title cell still carries a focusable `<Link>` for keyboard
              // navigation + screen reader announcement.
              onClick={(e) => {
                // Don't double-fire when the user clicked the inner
                // <Link> — Inertia handles that on its own.
                if ((e.target as HTMLElement).closest("a")) return
                router.get(analyticsMissionPath(row.id))
              }}
            >
              <TableCell>
                <Link
                  href={analyticsMissionPath(row.id)}
                  className="font-medium hover:underline"
                >
                  {row.title}
                </Link>
              </TableCell>
              <TableCell>
                {t(`analytics.filters.mission_type.${row.mission_type}`)}
              </TableCell>
              <TableCell className="text-right">{row.total_collabs}</TableCell>
              <TableCell className="text-right">
                {formatRate(row.completion_rate, locale)}
              </TableCell>
              <TableCell className="text-right">
                {row.approved_submissions}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function MobileCards({ rows, locale, t, className }: RowsProps) {
  return (
    <ul
      className={cn("flex flex-col gap-3", className)}
      data-testid="analytics-overview-top-missions-mobile-list"
    >
      {rows.map((row) => (
        <li
          key={row.id}
          className="bg-muted/30 hover:bg-muted/50 rounded-lg border p-3"
          data-testid={`analytics-overview-top-mission-card`}
        >
          <Link href={analyticsMissionPath(row.id)} className="block space-y-1">
            <p className="text-foreground font-medium">{row.title}</p>
            <p className="text-muted-foreground text-xs">
              {t(`analytics.filters.mission_type.${row.mission_type}`)}
            </p>
            <dl className="text-muted-foreground mt-2 grid grid-cols-3 gap-2 text-xs">
              <div>
                <dt>{t("analytics.top_missions.columns.total_collabs")}</dt>
                <dd className="text-foreground font-medium">
                  {row.total_collabs}
                </dd>
              </div>
              <div>
                <dt>{t("analytics.top_missions.columns.completion_rate")}</dt>
                <dd className="text-foreground font-medium">
                  {formatRate(row.completion_rate, locale)}
                </dd>
              </div>
              <div>
                <dt>
                  {t("analytics.top_missions.columns.approved_submissions")}
                </dt>
                <dd className="text-foreground font-medium">
                  {row.approved_submissions}
                </dd>
              </div>
            </dl>
          </Link>
        </li>
      ))}
    </ul>
  )
}

function EmptyState() {
  const { t } = useTranslation()
  return (
    <div
      data-testid="analytics-overview-top-missions-empty"
      className="text-muted-foreground flex flex-col items-center gap-2 py-8 text-sm"
    >
      <p className="text-foreground text-sm font-medium">
        {t("analytics.top_missions.empty.title")}
      </p>
      <p>{t("analytics.top_missions.empty.widen_window")}</p>
    </div>
  )
}

const EM_DASH = "—"

function formatRate(value: number | null, locale: string): string {
  if (value === null) return EM_DASH
  return new Intl.NumberFormat(locale, {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value)
}
